import { Slot } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { cn } from "@rekati/utils";

/*
 * shadcn's Button with this system's variants. `volt` is the call to action:
 * iForte yellow with ink words (13.4:1). Every size keeps a 44px touch target
 * from `md` up (WCAG 2.5.5); `sm` is for dense tables only.
 */
export const buttonVariants = cva(
  [
    "relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap font-medium select-none",
    "transition-[background-color,color,box-shadow,transform] duration-200 ease-out-expo",
    "disabled:pointer-events-none disabled:opacity-45 active:scale-[0.98]",
    "focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[1.15em]",
  ],
  {
    variants: {
      variant: {
        volt: "bg-volt text-volt-foreground hover:bg-volt-hover",
        solid: "bg-primary text-primary-foreground hover:opacity-90",
        outline: "border border-input bg-transparent text-foreground hover:bg-accent",
        ghost: "bg-transparent text-foreground hover:bg-accent",
        subtle: "bg-secondary text-secondary-foreground hover:bg-accent",
        destructive: "bg-destructive text-background hover:opacity-90",
        link: "h-auto px-0 text-foreground underline decoration-volt decoration-2 underline-offset-4 hover:decoration-foreground",
      },
      size: {
        sm: "h-8 rounded-sm px-3 text-sm",
        md: "h-11 rounded-md px-4 text-[0.95rem]",
        lg: "h-12 rounded-md px-5 text-base",
        xl: "h-14 rounded-lg px-7 text-lg",
        icon: "size-11 rounded-md",
        "icon-sm": "size-8 rounded-sm",
      },
    },
    compoundVariants: [{ variant: "link", className: "h-auto px-0" }],
    defaultVariants: { variant: "solid", size: "md" },
  },
);

export type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Swaps the label for `loadingText` and springs a spinner in; announces the wait. */
    loading?: boolean;
    /** What the button says while it works ("Sending…"); the label stays when omitted. */
    loadingText?: string;
  };

const widthSpring = { type: "spring" as const, bounce: 0.12, damping: 34, stiffness: 420 };

/** Cult UI's button spinner: a faint ring and a turning arc. */
function Spinner() {
  return (
    <svg viewBox="0 0 24 24" className="size-4 motion-safe:animate-spin" aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        className="opacity-25"
      />
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeDasharray="14 42"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * The loading state follows Cult UI's Halo Button: the button eases to its
 * new width, the label swaps to `loadingText`, and a spinner chip
 * springs in at the end. Buttons that never load render as a plain button.
 */
export function Button({
  className,
  variant,
  size,
  asChild,
  loading,
  loadingText,
  children,
  disabled,
  ...props
}: ButtonProps) {
  const reduce = useReducedMotion();
  const classes = cn(buttonVariants({ variant, size }), className);
  if (asChild) {
    return (
      <Slot.Root data-slot="button" className={classes} {...props}>
        {children}
      </Slot.Root>
    );
  }
  if (loading === undefined) {
    return (
      <button data-slot="button" className={classes} disabled={disabled} {...props}>
        {children}
      </button>
    );
  }
  // Motion owns these four names with other signatures.
  const {
    onDrag: _drag,
    onDragStart: _dragStart,
    onDragEnd: _dragEnd,
    onAnimationStart: _animStart,
    ...rest
  } = props;
  return (
    <motion.button
      layout={reduce ? false : "size"}
      // Measure only when the loading state flips; a button that merely moves with the page never slides.
      layoutDependency={loading}
      transition={{ layout: widthSpring }}
      data-slot="button"
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      <motion.span
        layout={reduce ? false : "position"}
        layoutDependency={loading}
        className="inline-flex items-center gap-2"
      >
        {loading && loadingText ? loadingText : children}
      </motion.span>
      <AnimatePresence initial={false}>
        {loading ? (
          <motion.span
            key="spin"
            aria-hidden="true"
            className="grid h-6 place-items-center overflow-hidden rounded-full bg-current/15"
            initial={reduce ? { opacity: 0, width: 24 } : { opacity: 0, scale: 0.6, width: 0 }}
            animate={{ opacity: 1, scale: 1, width: 24 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.6, width: 0 }}
            transition={widthSpring}
          >
            <Spinner />
          </motion.span>
        ) : null}
      </AnimatePresence>
    </motion.button>
  );
}
